'use client'

import { TextSearchInput } from '@/app/components/TextSearchInput'
import SettingsIcon from '@/app/icons/Settings/Settings'
import { EXTERNAL_LINKS, ROUTES } from '@/app/lib/constants'
import { useSearchInputContext } from '@/app/modules/course/contexts/InputSearchContext'
import { LanguageSelector } from '@/app/modules/shared/components/language-selector'
import { useAuth } from '@/app/providers/Auth/Auth'
import { UserRole } from '@/types/user'
import { Dropdown } from 'flowbite-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { MdChevronLeft, MdClose, MdMenu } from 'react-icons/md'
import { PrimaryButton } from '../../../../components/PrimaryButton'
import { HeaderMenu } from './HeaderMenu'
import { LinkSection } from './LinkSection'

export type HeaderProps = {
  transparent?: boolean
  hideSearchBar?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export const Header = ({
  transparent,
  hideSearchBar,
  ...props
}: HeaderProps) => {
  const router = useRouter()
  const {
    isAuthenticated = false,
    firstName,
    lastName,
    roles: userRoles,
  } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const t = useTranslations('header')
  const { isLargeSearchVisible } = useSearchInputContext()

  // TODO: add paths
  const _BY_THEME_ROUTES = {
    title: t('links.by_theme'),
    routes: [
      {
        title: t('links.trending'),
        route: '/',
      },
      {
        title: t('links.latest_topics'),
        route: '/',
      },
      {
        title: t('links.bootcamps'),
        route: '/',
      },
      {
        title: t('links.new_courses'),
        route: '/',
      },
      {
        title: t('links.free_courses'),
        route: '/',
      },
      {
        title: t('links.pay_in_instalments'),
        route: '/',
      },
    ],
  }

  const _BY_TOPIC_ROUTES = {
    title: t('links.by_topic'),
    routes: [
      { title: t('links.ai'), route: '/' },
      { title: t('links.product'), route: '/' },
      { title: t('links.engineering'), route: '/' },
      { title: t('links.marketing'), route: '/' },
      { title: t('links.languages'), route: '/' },
      { title: t('links.business'), route: '/' },
      { title: t('links.leadership'), route: '/' },
    ],
  }

  const MENU_ROUTES = {
    title: t('links.by_theme'),
    routes: [
      /*
      // TODO: enable when we have the routes
      {
        title: t('links.explore'),
        route: '',
        // TODO: add routes
        routes: [BY_THEME_ROUTES, BY_TOPIC_ROUTES],
      },
      */
    ],
  }

  const ACCOUNT_ROUTES = {
    title: t('links.account'),
    routes: [
      { title: t('links.profile'), route: ROUTES.myProfile },
      { title: t('links.my_orders'), route: ROUTES.myOrders },
      { title: t('links.financing'), route: ROUTES.myFinancing },
      ...([
        UserRole.EducationProvider,
        UserRole.Employer,
        UserRole.TaxBenefitsProvider,
      ].some((userRole) => (userRoles ?? []).includes(userRole))
        ? [
            {
              route: EXTERNAL_LINKS.partnerDashboard,
              icon: SettingsIcon,
              title: t('links.partner_dashboard'),
              target: '_blank',
            },
          ]
        : []),
      { title: t('links.logout'), route: ROUTES.logout },
    ],
  }

  const currentSearchTerm = router.query.term as string

  const handleSearch = (term: string) => {
    if (router.pathname === ROUTES.search) {
      router.push({
        pathname: router.pathname,
        query: { ...router.query, term },
      })
    } else {
      router.push({ pathname: ROUTES.search, query: { term } })
    }
  }

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [menuOpen])

  return (
    <header
      {...props}
      className={`${
        transparent ? 'bg-transparent' : 'bg-white'
      } sticky left-0 top-0 z-40 flex w-full shrink-0 flex-col py-3 md:py-5 ${props.className ?? ''}`}
    >
      <div className="container flex h-full min-h-[50px] flex-row items-center justify-end gap-x-4">
        <div className="mr-auto flex shrink-0 items-center gap-9">
          <Link href={ROUTES.homepage}>
            <Image
              src="/images/mydra-lg.png"
              width={1000}
              height={250}
              alt={t('logo_alt')}
              priority
              className="h-8 w-auto"
            />
          </Link>
          {MENU_ROUTES.routes.map(({ title, route, routes }) => (
            <Dropdown
              placement="bottom-start"
              key={route}
              label={title}
              className="rounded-lg shadow-none"
              renderTrigger={() => {
                const isLink =
                  route && Array.isArray(routes) && routes.length === 0

                const textContents = (
                  <div className="group relative hidden cursor-pointer items-center md:flex">
                    <span className="text-base font-semibold text-mydra-black">
                      {title}
                    </span>
                    <MdChevronLeft className="w-4 -rotate-90" />
                  </div>
                )

                return isLink ? (
                  <Link
                    href={route}
                    className=""
                    key={route}
                    data-dropdown-trigger="hover"
                  >
                    {textContents}
                  </Link>
                ) : (
                  textContents
                )
              }}
            >
              <div className="grid grid-cols-2 gap-20 p-6">
                {routes.map((route) => (
                  <LinkSection
                    key={route.title}
                    title={route.title}
                    routes={route.routes}
                  />
                ))}
              </div>
            </Dropdown>
          ))}
          {!isLargeSearchVisible && (
            <div className="mx-auto hidden w-[300px] sm:block md:w-[430px]">
              <TextSearchInput
                onSearch={handleSearch}
                defaultValue={currentSearchTerm || ''}
                hideFilters
              />
            </div>
          )}
        </div>

        <LanguageSelector
          useAbbreviatedLangName
          variant="button"
          className="md:hidden"
        />

        <LanguageSelector variant="button" className="hidden md:block" />

        {!isAuthenticated && (
          <div className="flex items-center justify-items-end gap-4">
            {!isAuthenticated && (
              <>
                <Link
                  className="block min-w-fit text-sm font-semibold text-mydra-black hover:opacity-60 max-sm:hidden"
                  href={{
                    pathname: ROUTES.login,
                    query: { redirectTo: router.pathname },
                  }}
                >
                  {t('login')}
                </Link>

                <PrimaryButton
                  id="header-signup-button--redirect-to-signup"
                  small
                  className="min-w-fit hover:opacity-85"
                  onClick={() =>
                    router.push({
                      pathname: ROUTES.signup,
                      query: { redirectTo: router.pathname },
                    })
                  }
                >
                  {t('sign_up')}
                </PrimaryButton>
              </>
            )}
          </div>
        )}

        {isAuthenticated && (
          <>
            <HeaderMenu
              variant="dropdown"
              sections={[ACCOUNT_ROUTES]}
              userInitials={[firstName, lastName]
                .filter(Boolean)
                .map((name) => name?.[0] ?? '')
                .join('')}
              userName={[firstName, lastName]
                .filter(Boolean)
                .map((name) => name?.[0] ?? '')
                .join(' ')}
            />

            <span className="flex md:hidden" onClick={() => setMenuOpen(true)}>
              <MdMenu className="h-6 w-6" />
            </span>
            {menuOpen && (
              <div className="fixed left-0 top-0 z-[999999] h-full w-full bg-white md:hidden">
                <div className="flex flex-col">
                  <div className="container flex flex-row items-center gap-4 py-3 md:py-5">
                    <Link href={ROUTES.homepage} className="mr-auto">
                      <Image
                        src="/images/mydra-lg.png"
                        width={1000}
                        height={250}
                        alt={t('logo_alt')}
                        priority
                        className="h-8 w-auto"
                      />
                    </Link>
                    <LanguageSelector
                      variant="button"
                      className="h-[50px]"
                      useAbbreviatedLangName
                    />
                    <button onClick={() => setMenuOpen(false)}>
                      <MdClose className="ml-auto h-6 w-6" />
                    </button>
                  </div>
                  <HeaderMenu
                    sections={[
                      // BY_THEME_ROUTES, // TODO: enable when we have the routes
                      // BY_TOPIC_ROUTES, // TODO: enable when we have the routes
                      // { separator: true }, // TODO: enable when we have the routes
                      ACCOUNT_ROUTES,
                    ]}
                    onClose={() => setMenuOpen(false)}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {!isLargeSearchVisible && !hideSearchBar && (
        <div className="container mt-3 sm:hidden">
          <TextSearchInput
            onSearch={handleSearch}
            defaultValue={currentSearchTerm || ''}
            noIcon
            hideFilters
          />
        </div>
      )}
    </header>
  )
}
